/**
 * Badge & level metrics — the visual encoding of a user's lifetime points.
 *
 * The level formula mirrors the backend (app/services/points_service.py
 * compute_level) exactly, so the UI never disagrees with the server.
 *
 * Tiers: 7 tiers × 10 levels each. The ring hue encodes the tier; within a
 * tier the ring brightens as the user climbs. See GAMIFICATION_REPORT.md .
 */

const LEVEL_STEP = Math.log(1.25);
const LEVEL_BASE = 10; // lifetime points at which level 2 begins

export interface Tier {
  name: string;
  hue: number;
  sat?: number;
}

/** Index = tierIndex (floor((level - 1) / 10)). */
export const TIERS: Tier[] = [
  { name: 'Bronze', hue: 30 },
  { name: 'Silver', hue: 215, sat: 20 },
  { name: 'Gold', hue: 45 },
  { name: 'Platinum', hue: 195 },
  { name: 'Diamond', hue: 280 },
  { name: 'Green', hue: 140 },
  { name: 'Red', hue: 350 },
];

/** Level from lifetime points (mirrors backend compute_level). */
export const calculateUserLevel = (lifetimePoints: number): number => {
  if (!Number.isFinite(lifetimePoints) || lifetimePoints < LEVEL_BASE) {
    return 1;
  }
  return Math.floor(Math.log(lifetimePoints / LEVEL_BASE) / LEVEL_STEP) + 2;
};

/** Minimum lifetime points required to reach a given level. */
export const pointsForLevel = (level: number): number => {
  if (level <= 1) return 0;
  return LEVEL_BASE * Math.exp(LEVEL_STEP * (level - 2));
};

/**
 * Progress (0..1) from the current level toward the next, given lifetime
 * points. Level 1 measures progress toward level 2's threshold.
 */
export const getLevelProgress = (lifetimePoints: number): number => {
  const level = calculateUserLevel(lifetimePoints);
  const floor = pointsForLevel(level);
  const ceil = pointsForLevel(level + 1);
  if (ceil <= floor) return 1;
  const fraction = (lifetimePoints - floor) / (ceil - floor);
  return Math.min(1, Math.max(0, fraction));
};

export interface BadgeStyles {
  level: number;
  tierIndex: number;
  tierName: string;
  rankInTier: number;
  hue: number;
  lightness: number;
  hasGlow: boolean; // Silver tier (level >= 11) and up
  hasSheen: boolean; // Gold tier (level >= 21) and up
  color: string; // primary ring hsl
  colorLight: string;
  colorDeep: string;
  ringGradient: string;
  chipGradient: string;
  glow: string;
}

/** Visual treatment for a level's badge ring + chip. */
export const getBadgeStyles = (level: number): BadgeStyles => {
  const safeLevel = Math.max(1, Math.floor(level || 1));
  const tierIndex = Math.min(
    TIERS.length - 1,
    Math.floor((safeLevel - 1) / 10),
  );
  const rankInTier = (safeLevel - 1) % 10;
  const { hue, name, sat } = TIERS[tierIndex];
  const s = sat ?? 90;
  const lightness = 75 - rankInTier * 3.33;
  const hasGlow = safeLevel >= 11;
  const hasSheen = safeLevel >= 21;

  const color = `hsl(${hue}, ${s}%, ${lightness}%)`;
  const colorLight = `hsl(${hue}, ${Math.min(100, s + 5)}%, ${Math.min(92, lightness + 14)}%)`;
  const colorDeep = `hsl(${hue}, ${Math.max(0, s - 5)}%, ${Math.max(28, lightness - 16)}%)`;

  const ringGradient =
    `conic-gradient(from 0deg, ${colorLight}, ${color}, ${colorDeep}, ` +
    `${color}, ${colorLight})`;
  const chipGradient = `linear-gradient(135deg, ${colorLight}, ${colorDeep})`;
  const glow = hasGlow
    ? `0 0 ${hasSheen ? 14 : 8}px hsla(${hue}, ${s}%, ${lightness}%, ${
        hasSheen ? 0.7 : 0.5
      })`
    : '';

  return {
    level: safeLevel,
    tierIndex,
    tierName: name,
    rankInTier,
    hue,
    lightness,
    hasGlow,
    hasSheen,
    color,
    colorLight,
    colorDeep,
    ringGradient,
    chipGradient,
    glow,
  };
};
